"use client";

import { cn } from "@marketing-sites/utils/cn";
import { Carousel } from "@marketing-sites/ui/primitives";

import {
  DotDecorators,
  type DotDecoratorsVariant,
} from "@marketing-sites/ui/components";
import {
  Testimonial,
  type TestimonialProps,
} from "@marketing-sites/ui/components";

export type CarouselTestimonialCard = Pick<
  TestimonialProps,
  "company" | "quote" | "author" | "image"
> & {
  analyticsId: string;
  thumbnailMetadata: { analyticsId: string };
};

export interface CarouselTestimonialProps {
  title: React.ReactNode;
  analyticsRegion?: string;
  cards: CarouselTestimonialCard[];
  titleClassName?: string;
  progressBarClassName?: string;
  cardBgClassName?: string;
  testimonialClassName?: string;
  tabBgClassName?: string;
  tabSelectedBgClassName?: string;
  verticalAlignment?: TestimonialProps["verticalAlignment"];
  sectionClassName?: string;
  gapClassName?: string;
  carouselClassName?: string;
  dotDecorators?: boolean;
  dotDecoratorClassName?: string;
  cardDotDecoratorVariant?: DotDecoratorsVariant;
  cardDotDecoratorClassName?: string;
  sectionMetadata: { analyticsId: string };
}

export default function CarouselTestimonial({
  title,
  analyticsRegion,
  cards,
  titleClassName = "mx-auto max-w-2xl text-balance text-center",
  progressBarClassName = "bg-helpdesk",
  cardBgClassName = "bg-chalk",
  testimonialClassName,
  tabBgClassName,
  tabSelectedBgClassName,
  verticalAlignment = "top",
  sectionClassName,
  gapClassName,
  carouselClassName,
  dotDecorators = true,
  dotDecoratorClassName,
  cardDotDecoratorVariant,
  cardDotDecoratorClassName,
  sectionMetadata,
}: CarouselTestimonialProps) {
  const region =
    analyticsRegion ?? (typeof title === "string" ? title : "Testimonials");

  return (
    <section
      data-analytics-region={region}
      data-analytics-id={sectionMetadata.analyticsId}
      className={cn(
        "container relative mx-auto py-10 md:py-16 lg:py-20",
        sectionClassName,
      )}
    >
      <div className="grid grid-cols-12 gap-x-4">
        <div
          className={cn(
            "col-span-full grid grid-cols-subgrid gap-x-4 gap-y-12 md:gap-y-20 xl:col-span-10 xl:col-start-2",
            gapClassName,
          )}
        >
          {title && (
            <div className="col-span-full">
              <h2 className={cn("text-heading-3", titleClassName)}>{title}</h2>
            </div>
          )}

          <div className="col-span-full">
            <Carousel.Root autoplayOptions={{ delay: 7500 }}>
              <Carousel.Thumbnails className="flex gap-0">
                {cards.map((card, index) => (
                  <Carousel.Thumbnail
                    key={card.company.name}
                    index={index}
                    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the call site
                    analyticsId={card.thumbnailMetadata.analyticsId}
                    className={cn(
                      "border-current/10 data-selected:z-1 data-selected:text-current data-selected:bg-chalk text-current/50 hover:text-current/75 hover:z-1 min-[1370px]:px-4! text-label-lg -ml-px flex w-full cursor-pointer items-center border border-b-0 p-2 py-3 text-left first:ml-0 md:p-4 md:px-6 lg:px-10",
                      tabBgClassName,
                      tabSelectedBgClassName,
                    )}
                    progress={({ ref, isPlaying, isSelected }) => (
                      <div
                        ref={ref}
                        className={cn(
                          progressBarClassName,
                          "absolute left-0 top-0 h-0.5 w-full origin-left",
                        )}
                        style={{
                          animation: "carouselAutoplay linear",
                          opacity: isPlaying && isSelected ? 1 : 0,
                        }}
                      />
                    )}
                  >
                    {card.company.name}
                  </Carousel.Thumbnail>
                ))}
              </Carousel.Thumbnails>

              <div
                className={cn(
                  "bg-clay relative overflow-hidden",
                  carouselClassName,
                )}
              >
                {/* Height measurement only, so no analytics ID: these copies
                    are hidden, and an ID here would repeat each visible card's. */}
                <div className="grid" aria-hidden>
                  {cards.map(
                    (
                      {
                        analyticsId: _analyticsId,
                        thumbnailMetadata: _thumbnail,
                        ...card
                      },
                      index,
                    ) => (
                      <div
                        key={card.company.name}
                        className="[grid-area:1/1]"
                        style={index > 0 ? { visibility: "hidden" } : undefined}
                      >
                        <Testimonial
                          dotDecorators={false}
                          verticalAlignment={verticalAlignment}
                          className={cn(
                            "bg-off-white opacity-0",
                            testimonialClassName,
                          )}
                          {...card}
                        />
                      </div>
                    ),
                  )}
                </div>

                <Carousel.Viewport
                  className={cn(cardBgClassName, "absolute inset-0")}
                >
                  <Carousel.Container className="h-full">
                    {cards.map(
                      ({
                        analyticsId,
                        thumbnailMetadata: _thumbnail,
                        ...card
                      }) => (
                        <Carousel.Slide
                          key={card.company.name}
                          className="h-full basis-full"
                        >
                          <Testimonial
                            dotDecorators
                            dotDecoratorVariant={cardDotDecoratorVariant}
                            dotDecoratorClassName={cardDotDecoratorClassName}
                            verticalAlignment={verticalAlignment}
                            className={cn(
                              cardBgClassName,
                              "border-current/10 h-full w-full border",
                              testimonialClassName,
                            )}
                            {...card}
                            // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the call site
                            analyticsId={analyticsId}
                          />
                        </Carousel.Slide>
                      ),
                    )}
                  </Carousel.Container>
                </Carousel.Viewport>
              </div>
            </Carousel.Root>
          </div>
        </div>
      </div>

      {dotDecorators && <DotDecorators className={dotDecoratorClassName} />}
    </section>
  );
}
